import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Flex,
  Loader,
  Skeleton,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useAuth0 } from "@auth0/auth0-react";
import { saveIdea } from "../../../../Api";
import BagelAutocomplete from "../../../general/BagelAutocomplete";
import { useKnowledgeData } from "../../../../hooks/useKnowledgeData";
import { useUserPermissions } from "../../../../hooks/useUserPermissions";
import { getInitials } from "../../../../utils/getInitials";
import { CLASS_NAMES } from "../../../../pages/AiHeader/constants";

export const OwnerCell: React.FC<any> = ({ data }) => {
  const { owners } = useKnowledgeData();
  const { canEditIdeasInline } = useUserPermissions();
  const auth0 = useAuth0();
  const isReadOnly = !canEditIdeasInline;

  const owner = data.owner || {};
  const initialName = owner.name || data.ownerName || "Unassigned";
  const initialId = (owner._id as string | undefined) || null;
  const initialPic = owner.picture || "";

  const [ownerName, setOwnerName] = useState(initialName);
  const [ownerId, setOwnerId] = useState<string | null>(initialId);
  const [ownerPicture, setOwnerPicture] = useState(initialPic);
  const [isSaving, setIsSaving] = useState(false);
  const saveSeqRef = useRef(0);

  useEffect(() => {
    const o = data.owner || {};
    setOwnerName(o.name || data.ownerName || "Unassigned");
    setOwnerId(o._id || null);
    setOwnerPicture(o.picture || "");
  }, [
    data._id,
    data.owner?._id,
    data.owner?.name,
    data.ownerName,
    data.owner?.picture,
  ]);

  const safeOwners = Array.isArray(owners) ? owners : [];

  return (
    <Box p={0} m={0} w="100%">
      <BagelAutocomplete
        disabled={isReadOnly}
        items={safeOwners.map((member: any) => ({
          name: member.name,
          id: member._id,
          useId: true,
          email: member.email,
          picture: member.picture,
        }))}
        selected={ownerId}
        onSelect={async (item: any) => {
          const seq = ++saveSeqRef.current;
          setIsSaving(true);
          const SAVE_TIMEOUT_MS = 30_000;
          try {
            const res = (await Promise.race([
              saveIdea(
                {
                  id: data._id,
                  ownerId: item.id,
                  extraFields: true,
                },
                auth0
              ),
              new Promise<never>((_, reject) =>
                setTimeout(
                  () => reject(new Error("Owner save timed out")),
                  SAVE_TIMEOUT_MS
                )
              ),
            ])) as Awaited<ReturnType<typeof saveIdea>>;

            if (seq !== saveSeqRef.current) return;

            // Match table cells like TagsCell: rely on axios throw for hard failures.
            // Only treat an explicit success: false as an error (many responses omit success).
            if (res?.data?.success === false) {
              showNotification({
                title: "Error saving owner",
                color: "red",
                message: "please try again...",
                icon: <IconX size="1.1rem" />,
                autoClose: 1200,
              });
              return;
            }

            setOwnerId(item?.id);
            setOwnerName(item?.name);
            setOwnerPicture(item?.picture || "");
            showNotification({
              title: "Owner saved",
              color: "green",
              message: "Owner saved successfully",
              icon: <IconCheck size="1.1rem" />,
              autoClose: 1200,
            });
          } catch (error: any) {
            if (seq !== saveSeqRef.current) return;
            console.error("Error saving owner:", error);
            showNotification({
              title: "Error saving owner",
              color: "red",
              message:
                error?.message === "Owner save timed out"
                  ? "Request timed out. Please try again."
                  : "please try again...",
              icon: <IconX size="1.1rem" />,
              autoClose: 2200,
            });
          } finally {
            if (seq === saveSeqRef.current) {
              setIsSaving(false);
            }
          }
        }}
      >
        <Tooltip
          label={
            <Box p={0.186}>
              <Text fz={11.4} mb={1.4}>
                Owner:
              </Text>
              <Text fz={11.4}>{ownerName}</Text>
            </Box>
          }
        >
          <Flex
            className={CLASS_NAMES.ownerMenu}
            h={24}
            px={8}
            py={4}
            mx={"2px"}
            justify="space-evenly"
            align="center"
            gap={6}
            sx={{
              borderRadius: "4px",
              cursor: isReadOnly ? "default" : "pointer",
            }}
          >
            {isSaving ? (
              <Loader
                size={14}
                color="#5C5CEB"
                className={CLASS_NAMES.ownerLoader}
              />
            ) : ownerName && ownerName !== "Unassigned" ? (
              <Avatar
                src={ownerPicture}
                radius="xl"
                size={16}
                className={CLASS_NAMES.ownerAvatar}
              >
                {getInitials(ownerName)}
              </Avatar>
            ) : null}
            <Text
              fz="13px"
              lineClamp={1}
              maw="150px"
              miw={"100px"}
              className={CLASS_NAMES.ownerName}
            >
              {ownerName || ""}
            </Text>
          </Flex>
        </Tooltip>
      </BagelAutocomplete>
    </Box>
  );
};

export const OwnerCellSkeleton: React.FC<{ col: any }> = ({ col }) => {
  const columnWidth = col.width;
  const minWidth = col.minWidth;
  const maxWidth = col.maxWidth;
  const nameWidth = columnWidth
    ? Math.min(parseInt(String(columnWidth), 10) * 0.5, 100)
    : 80;

  return (
    <Box
      sx={{
        width: columnWidth || "100%",
        minWidth,
        maxWidth,
      }}
    >
      <Flex
        h={24}
        px={8}
        py={4}
        mx={"2px"}
        align="center"
        gap={6}
        sx={{
          borderRadius: "4px",
        }}
      >
        <Skeleton circle height={16} width={16} />
        <Skeleton height={12} width={nameWidth} radius="sm" />
      </Flex>
    </Box>
  );
};
